import React, { useState, useEffect } from 'react';
import { 
  LineChart, 
  BarChart, 
  PieChart, 
  TrendingUp, 
  Users, 
  Calendar, 
  Star 
} from 'lucide-react';
import Card  from '@/components/ui/Card';
import  Button  from '@/components/ui/Button';
import ApiService from '../../../services/api';

const Analytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [timeframe, setTimeframe] = useState('30d');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, [timeframe]);

  const fetchAnalytics = async () => {
    try {
      const data = await ApiService.getAnalytics(timeframe);
      setAnalytics(data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Analytics</h2>
        <div className="flex gap-2">
          {['7d', '30d', '90d', '1y'].map((period) => (
            <Button
              key={period}
              variant={timeframe === period ? 'default' : 'outline'}
              onClick={() => setTimeframe(period)}
            >
              {period}
            </Button>
          ))}
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-gray-600">Total Revenue</h3>
            <TrendingUp className="w-5 h-5 text-green-500" />
          </div>
          <p className="text-3xl font-bold">${analytics?.revenue.total}</p>
          <p className="text-sm text-green-600 mt-2">
            +{analytics?.revenue.growth}% from last period
          </p>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-gray-600">New Customers</h3>
            <Users className="w-5 h-5 text-blue-500" />
          </div>
          <p className="text-3xl font-bold">{analytics?.customers.new}</p>
          <p className="text-sm text-blue-600 mt-2">
            +{analytics?.customers.growth}% from last period
          </p>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-gray-600">Appointments</h3>
            <Calendar className="w-5 h-5 text-purple-500" />
          </div>
          <p className="text-3xl font-bold">{analytics?.appointments.total}</p>
          <p className="text-sm text-purple-600 mt-2">
            {analytics?.appointments.completed} completed
          </p>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-gray-600">Customer Satisfaction</h3>
            <Star className="w-5 h-5 text-yellow-500" />
          </div>
          <p className="text-3xl font-bold">{analytics?.satisfaction.score}%</p>
          <p className="text-sm text-yellow-600 mt-2">
            Based on {analytics?.satisfaction.responses} responses
          </p>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Revenue Trend</h3>
          <div className="h-64">
            {/* Add revenue chart here */}
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Customer Growth</h3>
          <div className="h-64">
            {/* Add customer growth chart here */}
          </div>
        </Card>
      </div>

      {/* Additional Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Popular Services</h3>
          <div className="space-y-4">
            {analytics?.services.map((service) => (
              <div key={service.name} className="flex justify-between items-center">
                <span>{service.name}</span>
                <span className="font-medium">{service.bookings} bookings</span>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Customer Demographics</h3>
          <div className="space-y-4">
            {analytics?.demographics.map((demo) => (
              <div key={demo.category}>
                <div className="flex justify-between text-sm mb-1">
                  <span>{demo.category}</span>
                  <span>{demo.percentage}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-primary-600 h-2 rounded-full"
                    style={{ width: `${demo.percentage}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Communication Stats</h3>
          <div className="space-y-4">
            {analytics?.communications.map((comm) => (
              <div key={comm.channel} className="flex justify-between items-center">
                <span>{comm.channel}</span>
                <span className="font-medium">{comm.messages} messages</span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Analytics;